import { useState } from 'react'
import { campaignsApi, type CampaignCostImportResult } from '../api/campaigns'
import { Button, Callout, DataTable, Dialog, DialogSteps, FormGrid, TextField } from './ui'

// F-118 (REQ-104-001): "Importa costi" delle campagne. File CSV -> anteprima per riga -> conferma. L'applicazione
// e' tutta o niente: con una sola riga in errore il pulsante di conferma resta spento e il server rifiuterebbe comunque.
// I valori mostrati sono quelli del file appena caricato da chi sta importando (stessa policy della modifica manuale).

const MAX_BYTES = 1_000_000

const fmt = (v: number | null): string =>
  v === null ? '—' : v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

interface Props {
  onClose: () => void
  /** Chiamata dopo un'applicazione riuscita, per ricaricare l'elenco. */
  onApplied: () => void
}

export function CampaignCostImportDialog({ onClose, onApplied }: Props) {
  const [csv, setCsv] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<CampaignCostImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  // "Indietro" dall'anteprima: solo cambio di passo, il file e l'anteprima restano in memoria e nessuna chiamata viene rifatta.
  const [backToFile, setBackToFile] = useState(false)
  const step = done ? 3 : preview && !backToFile ? 2 : 1

  async function onFile(file: File | undefined) {
    setPreview(null)
    setError(null)
    setDone(false)
    setBackToFile(false)
    setCsv(null)
    setFileName('')
    if (!file) return
    if (file.size > MAX_BYTES) {
      setError('Il file è troppo grande (massimo 1 MB, 1.000 righe).')
      return
    }
    setBusy(true)
    try {
      const text = await file.text()
      setCsv(text)
      setFileName(file.name)
      setPreview(await campaignsApi.importCosts(text, true))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile leggere il file.')
    } finally {
      setBusy(false)
    }
  }

  async function apply() {
    if (csv === null || !preview || preview.errorRows > 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await campaignsApi.importCosts(csv, false)
      setPreview(res)
      setDone(res.applied)
      if (res.applied) onApplied()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Applicazione non riuscita: non è stato scritto nulla.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      as="form"
      onSubmit={(e) => { e.preventDefault(); if (step === 2) void apply() }}
      open
      onClose={onClose}
      title="Importa costi delle campagne"
      supportingText={
        <>
          File CSV (separatore <code>;</code> o <code>,</code>, massimo 1.000 righe) con le colonne{' '}
          <code>campagna</code> (id o nome esatto), <code>costo_totale</code>, <code>costo_per_lead</code>,{' '}
          <code>costo_fisso</code>, <code>costo_variabile</code>. Decimali all&apos;italiana (<code>1.234,50</code>) o
          con il punto (<code>1234.50</code>); una cella vuota lascia il valore com&apos;è. Prima di scrivere vedi
          l&apos;anteprima: se anche una sola riga è in errore non viene applicato nulla.
        </>
      }
      size="lg"
      actions={
        <>
          {step === 2 ? (
            <Button type="button" variant="text" disabled={busy} onClick={() => setBackToFile(true)}>
              Indietro
            </Button>
          ) : (
            <Button type="button" variant="text" dismiss onClick={onClose}>
              {done ? 'Chiudi' : 'Annulla'}
            </Button>
          )}
          {step === 1 && backToFile && preview ? (
            <Button type="button" variant="text" disabled={busy} onClick={() => setBackToFile(false)}>
              Avanti
            </Button>
          ) : (
            !done && (
              <Button
                type="submit"
                variant="text"
                disabled={busy || !preview || preview.errorRows > 0 || preview.okRows === 0}
              >
                {busy ? 'Attendere...' : 'Conferma e applica'}
              </Button>
            )
          )}
        </>
      }
    >
      <DialogSteps
        current={step}
        total={3}
        label={step === 3 ? 'Esito' : step === 2 ? 'Anteprima' : 'File'}
      />
      {error && <Callout tone="error" role="alert">{error}</Callout>}
      {step === 1 && (
        <FormGrid columns={1}>
          <TextField
            type="file"
            accept=".csv,text/csv,text/plain"
            label="File CSV dei costi"
            labelHidden
            disabled={busy}
            onChange={e => void onFile(e.target.files?.[0])}
          />
        </FormGrid>
      )}
      {done && (
        <Callout tone="success" role="status">
          Costi applicati a {preview?.okRows ?? 0} campagne.
        </Callout>
      )}

      {preview && step !== 1 && (
        <>
          <div className="md-typescale-body-small" aria-live="polite">
            {fileName && <strong>{fileName}</strong>}: {preview.totalRows} righe, {preview.okRows} corrette,{' '}
            <span className={preview.errorRows > 0 ? 'text-danger md-typescale-emphasized-body-small' : ''}>
              {preview.errorRows} in errore
            </span>
            .
          </div>
          <div className="md-dialog__bleed">
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Riga</th>
                  <th>Campagna</th>
                  <th className="text-end">Totale</th>
                  <th className="text-end">Per lead</th>
                  <th className="text-end">Fisso</th>
                  <th className="text-end">Variabile</th>
                  <th>Esito</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.row} style={r.status === 'error' ? { background: 'hsl(var(--md-error-container) / .5)' } : undefined}>
                    <td>{r.row}</td>
                    <td>{r.campaignName ?? r.campaign}</td>
                    <td className="text-end md-num">{fmt(r.totalCost)}</td>
                    <td className="text-end md-num">{fmt(r.perLeadCost)}</td>
                    <td className="text-end md-num">{fmt(r.fixedCost)}</td>
                    <td className="text-end md-num">{fmt(r.variableCost)}</td>
                    <td>{r.status === 'ok' ? (done ? 'Applicata' : 'OK') : r.error}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </>
      )}
    </Dialog>
  )
}
